import { Button, ButtonMode, Icon, ICONS, Loader, Modal, ModalWidth, Tooltip } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import { difference, orderBy, uniq } from 'lodash';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Form, Ref } from 'semantic-ui-react';
import { FormDropdown } from '../../../../components/FormDropdown';
import { useGlobalModalContext } from '../../../../components/GlobalModal';
import useFetchUserProfile from '../../../../hooks/profile/useFetchUserProfile';
import { useToast } from '../../../../hooks/toast/useToast';
import { useUpdateWatchList } from '../../../../hooks/watchLists';
import useShareWatchlistUsers from '../../../../hooks/watchLists/useShareWatchlistUsers';
import { WatchList } from '../../../../models/WatchList';
import { ShareWatchlistUsersDto } from '../../../../services/dtos/watchLists/shareWatchlistUsers.dto';
import styles from './ShareWatchlistModal.module.scss';

const cx = classNames.bind(styles);

interface ShareWatchlistModalProps {
  watchList: WatchList;
}

interface ShareWatchlistModalForm {
  user: string;
}

const ShareWatchlistModal = ({ watchList }: ShareWatchlistModalProps): React.ReactElement => {
  const { showToast } = useToast();
  const [shareUserIds, setShareUserIds] = useState<string[]>([]);
  const [sharedUsers, setSharedUsers] = useState<ShareWatchlistUsersDto[]>([]);
  const { control } = useForm<ShareWatchlistModalForm>({ defaultValues: { user: '' } });
  const { closeModal } = useGlobalModalContext();
  const { data: profile } = useFetchUserProfile();
  const { data: users, isLoading } = useShareWatchlistUsers();
  const { mutate: updateWatchlist, isLoading: isUpdatingWatchlist } = useUpdateWatchList();

  useEffect(() => {
    if (watchList.sharedWithUsers) {
      setShareUserIds([...watchList.sharedWithUsers]);
    }
  }, [watchList.sharedWithUsers]);

  useEffect(() => {
    if (profile && users && shareUserIds) {
      const map: any = {};
      const list: any[] = [];
      const currentUser = {
        id: '',
        name: `${profile.firstName} ${profile.lastName}`,
        email: profile.email,
      };

      users.forEach((user) => {
        map[user.id] = user;
      });

      shareUserIds.forEach((id) => {
        if (map[id]) {
          list.push(map[id]);
        }
      });

      setSharedUsers([currentUser].concat(orderBy(list, ['name'])));
    }
  }, [profile, users, shareUserIds]);
  const renderIcon = (user: ShareWatchlistUsersDto) => {
    return user.name
      .split(' ')
      .slice(0, 2)
      .map((p) => p.slice(0, 1))
      .join('');
  };

  const renderFooter = () => {
    if (isLoading) {
      return <span />;
    }

    return (
      <>
        <Button mode={ButtonMode.SECONDARY} onClick={closeModal} disabled={isUpdatingWatchlist}>
          Cancel
        </Button>
        <Button mode={ButtonMode.PRIMARY} onClick={save} loading={isUpdatingWatchlist}>
          Share/Unshare Watchlist
        </Button>
      </>
    );
  };

  const parseOptions = () => {
    return users?.map((user) => ({
      key: user.id,
      value: user.id,
      text: `${user.name} (${user.email})`,
    }));
  };

  const onChange = (value: string) => {
    if (users) {
      setShareUserIds((array) => {
        array.push(value);
        return uniq([...array]);
      });
    }
  };

  const save = () => {
    const oldSharedList = watchList.sharedWithUsers;
    const newSharedList = shareUserIds;
    updateWatchlist(
      {
        id: watchList.id,
        watchList: {
          ...watchList,
          sharedWithUsers: shareUserIds,
        },
      },
      {
        onSuccess: () => {
          closeModal();
          const toastMessage = getToastMessage(oldSharedList || [], newSharedList);
          showToast(toastMessage);
        },
      }
    );
  };

  const getToastMessage = (oldSharedList: string[], newSharedList: string[]) => {
    const deletedCount = difference(oldSharedList, newSharedList).length;
    const addedCount = difference(newSharedList, oldSharedList).length;

    if (newSharedList.length === 0) {
      return `${watchList.name} is now private. No one has access to it except you`;
    }
    if (deletedCount === 0 && addedCount > 0) {
      return `${watchList.name} was successfully shared with ${addedCount} new user(s)`;
    }
    if (deletedCount > 0 && addedCount === 0) {
      return `${watchList.name} was successfully unshared with ${deletedCount} user(s)`;
    }
    if (deletedCount > 0 && addedCount > 0) {
      return `${watchList.name} was successfully shared with ${addedCount} new and unshared with ${deletedCount} user(s)`;
    }

    return `${watchList.name} was not shared or unshared with any additional users`;
  };

  const removeUser = (id: string) => {
    const index = shareUserIds.indexOf(id);

    if (index !== -1) {
      setShareUserIds((array) => {
        array.splice(index, 1);
        return [...array];
      });
    }
  };

  return (
    <Modal
      width={ModalWidth.Medium}
      open
      footer={renderFooter()}
      onClose={closeModal}
      headerTitle={`Share Watchlist: ${watchList?.name}`}
    >
      <Form className={cx('body')}>
        {isLoading && <Loader text="processing" />}
        {!isLoading && users && (
          <>
            <div className={cx('field-ctx')}>
              <Controller
                name="user"
                control={control}
                render={({ field: { ref, ...rest } }) => (
                  <Ref innerRef={ref}>
                    <Form.Field
                      {...rest}
                      control={FormDropdown}
                      onOptionSelect={(value) => onChange(value)}
                      options={parseOptions()}
                      placeholder="Search by name or email address"
                      fluid
                      dataTestId="analysts-dropdown"
                    />
                  </Ref>
                )}
              />
            </div>
            <div className={cx('users-ctx')}>
              <table>
                <tbody>
                  {sharedUsers.map((user) => (
                    <tr key={user.id}>
                      <td>
                        <span className={cx('icon')}>{renderIcon(user)}</span>
                      </td>
                      <td>{user.name}</td>
                      <td>{!user.id ? '(You)' : user.email}</td>
                      <td>
                        {user.id && (
                          <Tooltip
                            content="Unshare"
                            trigger={
                              <Button onClick={() => removeUser(user.id)}>
                                <Icon name={ICONS.Delete} />
                              </Button>
                            }
                          />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Form>
    </Modal>
  );
};

export default ShareWatchlistModal;
